import React from 'react';
import { StyleSheet, View } from 'react-native';
import { APITypes } from '../../../../../../../primary-record-types';
import { getIntl } from '../../../../../../../utils/intl';
import Stack from '../../../../../../v2BaseComponents/Stack';
import { AvatarStack, GenericAvatarStackItem, getFallbackFromName } from '../../../../../../v2BaseComponents/Avatar';
import * as Card from '../../../../../../v2BaseComponents/Card';
import { useIsMobileLayout } from '../../../../../../../hooks/useIsMobileLayout';
import _chunk from 'lodash/chunk';
import { useCollaborators } from '../../../../../../../hooks/api/useCollaborators';
import urls from '../../../../../../../utils/urls';
import { useHasPermission } from '../../../../../../v2BaseComponents/Permissions/hooks';
import useMedicalProfileOwner from '../../../../../../../hooks/api/useMedicalProfileOwner';
import SkeletonLoadingContainer from '../../../../../../v2BaseComponents/SkeletonLoadingContainer';
import getName from '../../../../../../../utils/getName';
import { getCollaboratorName } from '../../../../../../../utils/collaboratorUtils';
import { useProfileUnreadInAppNotificationCount } from '../../../../../../../hooks/api/useProfileInUnreadAppNotificationCount';
import NotificationCountIndicator from '../../../../../../v2BaseComponents/NotificationIndicator/NotificationCountIndicator';

const intl = getIntl('profile-item', {
    invite: 'Invite',
    sharedAccessWith: 'Shared Access With:',
    alsoSharedAccessWith: 'Also Shared Access With:',
    profileOwner: 'Profile Owner: {name}',
    notShared: 'Not Shared',
});

export const PROFILE_ITEM_WIDTH = 100;

const styles = StyleSheet.create({
    container: {
        width: PROFILE_ITEM_WIDTH,
    },
});

export type ProfileItemOwnProps = {
    profile: APITypes.medicalProfileService.ProfileListItem;
    onPress: () => void;
    isInvite?: boolean;
    avatarUrl: string;
    isCollaboratorProfile: boolean;
};

export type ProfileItemProps = ProfileItemOwnProps;

export function useChunkProfiles<TItem>(profiles: TItem[]): (TItem | null)[][] {
    const columnCount = useIsMobileLayout() ? 1 : 2;
    return _chunk(profiles, columnCount).map((chunk) => {
        const missingItems = columnCount - chunk.length;
        return [...chunk, ...Array(missingItems).fill(null)];
    });
}

const COLLABORATOR_LIST_HEIGHT = 51;

const CollaboratorList: React.FC<{ medicalProfileId: string; isCollaboratorProfile: boolean }> = ({
    medicalProfileId,
    isCollaboratorProfile,
}) => {
    const { collaborators, isLoadingCollaborators } = useCollaborators({
        medicalProfileId,
    });

    if (isLoadingCollaborators || !collaborators?.length)
        return (
            <View style={{ height: COLLABORATOR_LIST_HEIGHT, justifyContent: 'flex-start' }}>
                <Card.CardBody2>{intl('notShared')}</Card.CardBody2>
            </View>
        );

    return (
        <Stack vertical align="flex-start" space={1} style={{ height: COLLABORATOR_LIST_HEIGHT }}>
            <Card.CardBody2>{intl(isCollaboratorProfile ? 'alsoSharedAccessWith' : 'sharedAccessWith')}</Card.CardBody2>
            <AvatarStack
                items={collaborators.map<GenericAvatarStackItem>((_) => ({
                    type: 'generic',
                    avatarAPIUrl: urls.api.collaborators.collaboratorAvatar(
                        medicalProfileId,
                        _.medicalProfileCollaboratorId,
                    ),
                    fallbackName: getFallbackFromName(getCollaboratorName(_)),
                }))}
                displayLimit={5}
            />
        </Stack>
    );
};

const ProfileOwnerSection: React.FC<{ medicalProfileId: string }> = ({ medicalProfileId }) => {
    const { owner, isLoading } = useMedicalProfileOwner({ medicalProfileId });

    const maybePlaceholderName = isLoading ? 'John Doe' : owner ? getName(owner) : '';

    if (!isLoading && !owner) return null;

    return (
        <SkeletonLoadingContainer isLoading={isLoading}>
            <Card.CardBody2>
                {intl('profileOwner', {
                    name: maybePlaceholderName,
                })}
            </Card.CardBody2>
        </SkeletonLoadingContainer>
    );
};

const BaseCard: React.FC<
    ProfileItemOwnProps & { leftContent: React.ReactNode | null; subContent: React.ReactNode | null }
> = ({ profile, onPress, isInvite, isCollaboratorProfile, leftContent, subContent }) => {
    const name = profile.preferredFirstName || profile.firstName;
    const { unreadInAppNotificationCount } = useProfileUnreadInAppNotificationCount({
        medicalProfileId: profile.medicalProfileId,
    });
    return (
        <Card.Card
            onPress={onPress}
            testID={`profile-picker${isInvite ? '-invite' : ''}-${name}`}
            style={{ height: isCollaboratorProfile ? 133 : 117, width: '100%' }}
        >
            <Card.CardLeftContent
                style={{ height: '100%' }}
                leftContent={
                    <Stack vertical fullHeight justify="center" style={{ marginRight: 8 }}>
                        {leftContent}
                    </Stack>
                }
            >
                <Card.CardRightContent
                    style={{
                        height: '100%',
                    }}
                    rightContent={
                        <Stack vertical fullHeight justify="flex-start">
                            {unreadInAppNotificationCount > 0 ? (
                                <NotificationCountIndicator count={unreadInAppNotificationCount} />
                            ) : null}
                        </Stack>
                    }
                >
                    <View
                        style={{
                            flex: 1,
                            height: '100%',
                            justifyContent: 'space-around',
                        }}
                    >
                        <Card.CardHeader>{name}</Card.CardHeader>
                        {isCollaboratorProfile && !isInvite ? (
                            <ProfileOwnerSection medicalProfileId={profile.medicalProfileId} />
                        ) : null}
                        {subContent || <View style={{ height: COLLABORATOR_LIST_HEIGHT }} />}
                    </View>
                </Card.CardRightContent>
            </Card.CardLeftContent>
        </Card.Card>
    );
};

const NonInviteProfileItem: React.FC<ProfileItemProps> = (props) => {
    const canViewCollaboratorList = useHasPermission(props.profile.medicalProfileId, 'collaborators', 'view');
    const name = props.profile.preferredFirstName || props.profile.firstName;

    return (
        <BaseCard
            {...props}
            leftContent={
                <Card.CardLoadableAvatar
                    size="xl"
                    avatarUrl={props.avatarUrl}
                    fallbackName={getFallbackFromName(name || '', props.profile.lastName || '')}
                />
            }
            subContent={
                canViewCollaboratorList ? (
                    <CollaboratorList
                        medicalProfileId={props.profile.medicalProfileId}
                        isCollaboratorProfile={props.isCollaboratorProfile}
                    />
                ) : null
            }
        />
    );
};

const InviteProfileItem: React.FC<ProfileItemProps> = (props) => {
    const name = props.profile.preferredFirstName || props.profile.firstName;

    return (
        <BaseCard
            {...props}
            leftContent={
                <Card.CardLoadableAvatar
                    size="xl"
                    avatarUrl={props.avatarUrl}
                    fallbackName={getFallbackFromName(name || '', props.profile.lastName || '')}
                />
            }
            subContent={
                <Stack horizontal justify="flex-start">
                    <Card.CardChip highlight title={intl('invite')} />
                </Stack>
            }
        />
    );
};

const ProfileItem: React.FC<ProfileItemProps> = (props) => {
    return props.isInvite ? <InviteProfileItem {...props} /> : <NonInviteProfileItem {...props} />;
};

export default React.memo(ProfileItem);

export function ProfileItemSpacer() {
    return <View style={styles.container} />;
}
